import { Injectable, inject } from '@angular/core';

import { EdgeBridgeService } from '../edge/edge-bridge.service';

/**
 * Durable device identity for the web app, independent of the edge client's
 * transient login URL param.
 *
 * Background: the hardware Edge Client id historically arrived only via the
 * `?source=edgeclient&deviceId=…` login URL and was kept in `sessionStorage`,
 * which the POS-boot clear and reloads wipe. This service makes identity
 * resilient:
 *
 *   • Authoritative source — the Flutter shell's `device:info` bridge call,
 *     re-fetchable any time (survives a full storage clear).
 *   • Sync cache — `localStorage` so the HTTP interceptor can read the id
 *     synchronously and so non-edge reloads keep it.
 *   • Legacy fallback — the existing `sessionStorage.edgeClientDeviceId`
 *     captured by the registration flow.
 *   • Browser-only — the `"web"` sentinel.
 *
 * It also caches the backend-resolved `edge_client_devices.Id` GUID (from
 * `GET /api/edge-clients/me/payment-status`) so components can reference the
 * device without re-resolving.
 */
const LS_EDGE_CLIENT_ID = 'edgeClientId';
const SS_LEGACY_KEY = 'edgeClientDeviceId';
const WEB_SENTINEL = 'web';

@Injectable({ providedIn: 'root' })
export class DeviceIdentityService {
  private readonly edge = inject(EdgeBridgeService);

  private edgeClientId: string | null = null;
  private resolvedDeviceGuid: string | null = null;

  /**
   * Called once during APP_INITIALIZER. Resolves the hardware id, preferring
   * the bridge, then persists it for synchronous reads. Never throws — a
   * failure just leaves the web sentinel in effect.
   */
  async init(): Promise<void> {
    // 1. Bridge — authoritative when running inside the Flutter shell.
    //
    // DEVICE-ID-CLONE-GAP — do NOT gate this on isEdgeEmbedded(): that reads
    // a BehaviorSubject that is still false at the synchronous instant this
    // initializer runs (shim detection completes a tick later), which made
    // this whole branch dead code and left a stale cloned localStorage id in
    // force. Instead, when there is any hint we are inside the shell, await
    // edge.call() directly — it internally waits for shim readiness (2s
    // budget) and rejects cleanly in a plain browser. The hint check keeps
    // plain-browser boots instant (no 2s shim-wait penalty).
    try {
      if (this.hasEmbedHint()) {
        // Short per-call timeout: this await sits inside APP_INITIALIZER, so
        // the default 10s bridge timeout would blank the whole SPA if the
        // Dart handler ever hangs. 3s comfortably covers a healthy shell
        // (shim wait ≤2s + a fast native read) and the caches remain the
        // fallback on expiry.
        const info = await this.edge.call('device:info', undefined, {
          timeoutMs: 3_000,
        });
        if (info?.edgeClientId && info.edgeClientId.trim().length > 0) {
          this.setEdgeClientId(info.edgeClientId.trim());
          return;
        }
      }
    } catch {
      // Bridge unavailable / handler missing — fall through to caches.
    }

    // 2. Caches: localStorage, then the legacy registration sessionStorage.
    const cached = this.readLocal() ?? this.readLegacySession();
    if (cached) {
      this.setEdgeClientId(cached);
    }
  }

  /**
   * Adopt an id as the durable device identity, overwriting any cached or
   * persisted value. Called by the registration flow with the exact id the
   * edge_client_devices row was registered under, so the identity used on
   * later check/print calls can never diverge from the registered row
   * (stale cloned localStorage would otherwise win the precedence chain).
   */
  adopt(id: string): void {
    const v = id.trim();
    if (v.length > 0) this.setEdgeClientId(v);
  }

  /**
   * Cheap synchronous signals that we are (or were) running inside the
   * Flutter shell: the shell always boots the SPA with ?source=edgeclient,
   * the WebView JS shim may already be installed, and a persisted id only
   * ever exists on shell-driven devices.
   */
  private hasEmbedHint(): boolean {
    try {
      if (typeof window !== 'undefined') {
        if (window.flutter_inappwebview) return true;
        const source = new URLSearchParams(window.location.search).get('source');
        if (source === 'edgeclient') return true;
      }
    } catch {
      /* fall through */
    }
    return (this.readLocal() ?? this.readLegacySession()) !== null;
  }

  /**
   * Synchronous accessor for the HTTP interceptor. Prefers the in-memory
   * value, then localStorage, then the legacy sessionStorage key, then the
   * web sentinel. Defensive against sandboxed envs without storage.
   */
  getEdgeClientId(): string {
    return this.edgeClientId ?? this.readLocal() ?? this.readLegacySession() ?? WEB_SENTINEL;
  }

  /** True when this session maps to a real registered device (not the sentinel). */
  isRealDevice(): boolean {
    return this.getEdgeClientId() !== WEB_SENTINEL;
  }

  /** Backend-resolved edge_client_devices.Id GUID, when known. */
  getResolvedDeviceGuid(): string | null {
    return this.resolvedDeviceGuid;
  }

  /** Cache the backend-resolved device GUID (e.g. from me/payment-status). */
  setResolvedDeviceGuid(guid: string | null): void {
    this.resolvedDeviceGuid = guid && guid.trim().length > 0 ? guid.trim() : null;
  }

  private setEdgeClientId(value: string): void {
    this.edgeClientId = value;
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_EDGE_CLIENT_ID, value);
    } catch {
      /* sandboxed iframe — in-memory only */
    }
  }

  private readLocal(): string | null {
    try {
      if (typeof localStorage !== 'undefined') {
        const v = localStorage.getItem(LS_EDGE_CLIENT_ID);
        if (v && v.trim().length > 0) return v.trim();
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private readLegacySession(): string | null {
    try {
      if (typeof sessionStorage !== 'undefined') {
        const v = sessionStorage.getItem(SS_LEGACY_KEY);
        if (v && v.trim().length > 0) return v.trim();
      }
    } catch {
      /* ignore */
    }
    return null;
  }
}
